import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  ListOpts,
  Page,
  PageRef,
  PopularOpts,
  RecommendationOpts,
  SearchQuery,
  SourceAuth,
  SourceCommentOpts,
  SourceCommentPage,
} from '../types.ts';
import { ParseError } from '../../lib/errors.ts';
import { createVeneraRuntime } from './bridge.ts';
import { normalizeVeneraError } from './errors.ts';
import {
  firstChapterId,
  mapVeneraCommentPage,
  mapOptionListToSearchFilters,
  mapVeneraComicDetails,
  mapVeneraPage,
  mapVeneraPageResult,
  pageFromListOpts,
} from './mapper.ts';
import { createVeneraPluginStorage } from './storage.ts';
import { runVeneraFormLogin, validateVeneraAuth } from './auth.ts';
import type { VeneraComicSource, VeneraLoadOptions, VeneraRuntime } from './types.ts';

export async function loadVeneraPlugin(options: VeneraLoadOptions): Promise<VeneraComicSource> {
  const storage = createVeneraPluginStorage(options.sourceId);
  const runtime = await createVeneraRuntime({ ...options, storage });
  const searchFilters = options.searchFilters ?? mapOptionListToSearchFilters(runtime.plugin.search?.optionList);
  const recommendationOptions = options.recommendationExploreKeys?.map(
    ({ exploreKey: _exploreKey, ...option }) => option,
  );
  const recommendationExploreKeys = new Map(
    options.recommendationExploreKeys?.map((option) => [option.id, option.exploreKey]) ?? [],
  );

  async function withErrors<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (err) {
      throw normalizeVeneraError(err, options.sourceId);
    }
  }

  async function search(query: SearchQuery, auth?: SourceAuth): Promise<Page<ComicCard>> {
    return withErrors(async () => {
      const page = Math.max(1, query.page ?? 1);
      if (isEmptyVeneraSearch(query)) {
        const result = await loadExplore(runtime, options.defaultExploreKey, page, auth);
        return mapVeneraPageResult(result, options.sourceId, { page });
      }
      const result = await runtime.call(
        ['__veneraPlugin', 'search', 'load'],
        [query.q ?? '', pluginOptions(query), page],
        auth,
      );
      return mapVeneraPageResult(result, options.sourceId, { page });
    });
  }

  async function getPopular(opts?: PopularOpts, auth?: SourceAuth): Promise<Page<ComicCard>> {
    return withErrors(async () => {
      const page = Math.max(1, opts?.page ?? 1);
      const result = await loadExplore(runtime, opts?.range ?? options.defaultExploreKey ?? 'popular', page, auth);
      return mapVeneraPageResult(result, options.sourceId, { page });
    });
  }

  async function getRecommendations(
    opts?: RecommendationOpts,
    auth?: SourceAuth,
  ): Promise<Page<ComicCard>> {
    return withErrors(async () => {
      const page = Math.max(1, opts?.page ?? 1);
      const selectedOptionId =
        opts?.optionId ??
        recommendationOptions?.find((option) => option.default)?.id ??
        recommendationOptions?.[0]?.id;
      const exploreKey =
        selectedOptionId !== undefined
          ? recommendationExploreKeys.get(selectedOptionId)
          : undefined;
      const result = await loadExplore(runtime, exploreKey ?? options.defaultExploreKey ?? 'popular', page, auth);
      return mapVeneraPageResult(result, options.sourceId, { page });
    });
  }

  async function getComic(id: string, auth?: SourceAuth): Promise<ComicDetails> {
    return withErrors(async () => {
      const result = await runtime.call(['__veneraPlugin', 'comic', 'loadInfo'], [id], auth);
      return mapVeneraComicDetails(result, options.sourceId, id);
    });
  }

  async function getPages(id: string, auth?: SourceAuth, opts?: { readUnitId?: string }): Promise<PageRef[]> {
    return withErrors(async () => {
      const details = await runtime.call(['__veneraPlugin', 'comic', 'loadInfo'], [id], auth);
      const chapterId = opts?.readUnitId ?? firstChapterIdForSource(details, options.sourceId);
      const result = await runtime.call(['__veneraPlugin', 'comic', 'loadEp'], [id, chapterId], auth);
      const pages = normalizePageList(result);
      if (!Array.isArray(pages)) {
        throw new ParseError('Venera episode result must be an array', { source: options.sourceId });
      }
      return pages.map((page, index) => ({
        ...mapVeneraPage(page, options.sourceId, id, index),
        readUnitId: chapterId,
      }));
    });
  }

  async function getComments(
    id: string,
    auth?: SourceAuth,
    opts?: SourceCommentOpts,
  ): Promise<SourceCommentPage> {
    return withErrors(async () => {
      const page = Math.max(1, opts?.page ?? 1);
      const result = await runtime.call(
        ['__veneraPlugin', 'comic', 'loadComments'],
        [id, id, page, opts?.parentId],
        auth,
      );
      return mapVeneraCommentPage(result, options.sourceId, { page, parentId: opts?.parentId });
    });
  }

  async function resolveImageUrl(ref: PageRef, _auth?: SourceAuth): Promise<{ url: string }> {
    if (!ref.resolvedUrl) {
      throw new ParseError('Venera page ref is missing resolvedUrl', { source: options.sourceId });
    }
    return { url: ref.resolvedUrl };
  }

  async function validateAuth(auth: SourceAuth): Promise<{ ok: boolean; username?: string; reason?: string }> {
    return validateVeneraAuth(options.sourceId, runtime, auth);
  }

  async function listFavorites(auth: SourceAuth, opts: ListOpts): Promise<Page<ComicCard>> {
    return withErrors(async () => {
      const page = pageFromListOpts(opts);
      const result = await runtime.call(
        ['__veneraPlugin', 'favorites', 'loadComics'],
        [page, opts.category ?? 0],
        auth,
      );
      return mapVeneraPageResult(result, options.sourceId, { page });
    });
  }

  async function addFavorite(id: string, auth: SourceAuth, category?: number): Promise<void> {
    await withErrors(async () => {
      await runtime.call(
        ['__veneraPlugin', 'favorites', 'addOrDelFavorite'],
        [id, category ?? 0, true],
        auth,
      );
    });
  }

  async function removeFavorite(id: string, auth: SourceAuth): Promise<void> {
    await withErrors(async () => {
      await runtime.call(
        ['__veneraPlugin', 'favorites', 'addOrDelFavorite'],
        [id, 0, false],
        auth,
      );
    });
  }

  const source: VeneraComicSource = {
    id: options.sourceId,
    name: typeof runtime.plugin.name === 'string' ? runtime.plugin.name : options.sourceId,
    description: typeof runtime.plugin.description === 'string' ? runtime.plugin.description : undefined,
    capabilities: options.capabilities,
    searchFilters,
    recommendationOptions,
    search,
    getPopular,
    ...(options.capabilities.recommendations ? { getRecommendations } : {}),
    getComic,
    getPages,
    ...(options.capabilities.comments ? { getComments } : {}),
    resolveImageUrl,
    validateAuth,
    listFavorites,
    addFavorite,
    removeFavorite,
    loginWithForm: (request) =>
      runVeneraFormLogin(options.sourceId, runtime, request, options.saveAuth),
    storage,
    testInternals: {
      callPluginMethod(methodName, args = [], auth) {
        return withErrors(() => runtime.call(['__veneraPlugin', ...methodName.split('.')], args, auth));
      },
    },
  };

  return source;
}

function firstChapterIdForSource(details: unknown, sourceId: ComicSource['id']): string {
  try {
    return firstChapterId(details, sourceId) ?? 'default';
  } catch (err) {
    throw new ParseError('Venera comic details contain invalid chapters', {
      source: sourceId,
      cause: err,
    });
  }
}

function pluginOptions(query: SearchQuery): unknown {
  if (Array.isArray(query.extra?.options)) return query.extra.options;
  if (typeof query.extra?.q_type === 'string') return [query.extra.q_type];
  if (typeof query.extra?.sort === 'string') return [query.extra.sort];
  if (typeof query.sort === 'string') return [query.sort];
  return query.extra ?? {};
}

function isEmptyVeneraSearch(query: SearchQuery): boolean {
  const hasQuery = typeof query.q === 'string' && query.q.trim().length > 0;
  if (hasQuery) return false;
  if (query.uploader) return false;
  if (query.language) return false;
  if (query.sort) return false;
  if ((query.includeTags?.length ?? 0) > 0) return false;
  if ((query.excludeTags?.length ?? 0) > 0) return false;
  const extra = query.extra ?? {};
  return Object.keys(extra).every((key) => key === 'page' || key === 'cursor');
}

async function loadExplore(
  runtime: VeneraRuntime,
  range: string | undefined,
  page: number,
  auth?: SourceAuth,
): Promise<unknown> {
  if (Array.isArray(runtime.plugin.explore)) {
    const selectedIndex = selectExploreIndex(runtime.plugin.explore, range);
    return runtime.call(['__veneraPlugin', 'explore', String(selectedIndex), 'load'], [page], auth);
  }
  return runtime.call(['__veneraPlugin', 'explore', 'load'], [range ?? 'default', page], auth);
}

function selectExploreIndex(explore: unknown[], range: string | undefined): number {
  if (range) {
    const index = explore.findIndex((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
      return (entry as { title?: unknown }).title === range;
    });
    if (index >= 0) return index;
  }
  const loadableIndex = explore.findIndex(
    (entry) => entry !== null && typeof entry === 'object' && !Array.isArray(entry) && 'load' in entry,
  );
  return Math.max(0, loadableIndex);
}

function normalizePageList(value: unknown): unknown[] | undefined {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return undefined;
  const images = (value as { images?: unknown }).images;
  return Array.isArray(images) ? images : undefined;
}
